'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { LearnerLevel, LearningModuleDto } from '@vuganawe/types';

import { CourseCard } from '@/components/learning/CourseCard';
import { useT } from '@/i18n/client';
import { LEVEL_CEFR, PLACEMENT_QUESTIONS, recommendLevel, SECONDS_PER_QUESTION } from '@/content/placement';
import { getLearningModules } from '@/lib/api/public';

type Stage = 'intro' | 'quiz' | 'result';

/** Short beat after picking so the choice is visible before auto-advancing. */
const ADVANCE_DELAY_MS = 550;

function formatClock(seconds: number): string {
  const safe = Math.max(seconds, 0);
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** True when the browser can speak text aloud (used for listening items). */
function ttsAvailable(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/** Speak an English sentence aloud, a touch slower for clarity. */
function speak(text: string): void {
  if (!ttsAvailable()) return;
  try {
    const synth = window.speechSynthesis;
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 0.95;
    synth.speak(utterance);
  } catch {
    /* speech unavailable — the read-aloud fallback covers it */
  }
}

function stopSpeaking(): void {
  if (!ttsAvailable()) return;
  try {
    window.speechSynthesis.cancel();
  } catch {
    /* ignore */
  }
}

/**
 * CEFR-aligned placement screener: one question at a time, each on its own
 * timer, then a recommended starting level with matching courses. Picking an
 * answer auto-advances; learners can go back to change earlier answers. The
 * result is kept in localStorage so we can greet returning learners later.
 */
export function PlacementTest() {
  const t = useT();
  const [stage, setStage] = useState<Stage>('intro');
  const [index, setIndex] = useState(0);
  /** The learner's picked option per question (undefined = skipped). */
  const [picks, setPicks] = useState<(string | undefined)[]>([]);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [level, setLevel] = useState<LearnerLevel | null>(null);
  const [courses, setCourses] = useState<LearningModuleDto[] | null>(null);
  // Assume TTS until we confirm on the client, so SSR doesn't flash the fallback.
  const [ttsSupported, setTtsSupported] = useState(true);

  const question = PLACEMENT_QUESTIONS[index];
  const total = PLACEMENT_QUESTIONS.length;
  const chosen = picks[index];
  const questionTime = question?.seconds ?? SECONDS_PER_QUESTION;

  // Latest advance fn + a pending pick-timer, kept in refs so the countdown
  // interval and the pick delay always run the current, freshest logic.
  const advanceRef = useRef<() => void>(() => {});
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAdvanceTimer = useCallback(() => {
    if (advanceTimer.current) {
      clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
  }, []);

  const finish = useCallback(() => {
    const score = PLACEMENT_QUESTIONS.reduce((sum, q, i) => sum + (picks[i] === q.answer ? 1 : 0), 0);
    const recommended = recommendLevel(score);
    setLevel(recommended);
    setStage('result');
    try {
      window.localStorage.setItem('vn-placement-level', recommended);
    } catch {
      /* private mode — fine */
    }
  }, [picks]);

  const goNext = useCallback(() => {
    clearAdvanceTimer();
    stopSpeaking();
    if (index + 1 < total) setIndex(index + 1);
    else finish();
  }, [clearAdvanceTimer, index, total, finish]);

  const goBack = useCallback(() => {
    clearAdvanceTimer();
    stopSpeaking();
    if (index > 0) setIndex(index - 1);
  }, [clearAdvanceTimer, index]);

  // Keep the ref pointed at the freshest advance logic for the timers to call.
  useEffect(() => {
    advanceRef.current = goNext;
  }, [goNext]);

  // Pick an answer: record it, then auto-advance after a short beat. Picking a
  // different option before it advances simply reschedules — nothing is locked.
  function pick(option: string) {
    setPicks((current) => {
      const next = [...current];
      next[index] = option;
      return next;
    });
    clearAdvanceTimer();
    advanceTimer.current = setTimeout(() => advanceRef.current(), ADVANCE_DELAY_MS);
  }

  function begin() {
    setPicks([]);
    setIndex(0);
    setSecondsLeft(PLACEMENT_QUESTIONS[0]?.seconds ?? SECONDS_PER_QUESTION);
    setStage('quiz');
  }

  function reset() {
    clearAdvanceTimer();
    stopSpeaking();
    setStage('intro');
    setIndex(0);
    setPicks([]);
    setLevel(null);
    setCourses(null);
  }

  // Confirm text-to-speech support on the client (for listening items).
  useEffect(() => {
    setTtsSupported(ttsAvailable());
  }, []);

  // Per-question countdown (listening items get a longer allowance). When it
  // reaches zero the test auto-advances, keeping whatever was picked.
  useEffect(() => {
    if (stage !== 'quiz') return;
    const q = PLACEMENT_QUESTIONS[index];
    if (!q) return;
    let remaining = q.seconds ?? SECONDS_PER_QUESTION;
    setSecondsLeft(remaining);
    const id = setInterval(() => {
      remaining -= 1;
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(id);
        advanceRef.current();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [index, stage]);

  // Auto-play the clip when a listening question appears; stop it on the way out.
  useEffect(() => {
    if (stage !== 'quiz') return;
    const q = PLACEMENT_QUESTIONS[index];
    if (q?.audio) speak(q.audio);
    return () => stopSpeaking();
  }, [index, stage]);

  // Drop any pending pick-timer on unmount.
  useEffect(() => clearAdvanceTimer, [clearAdvanceTimer]);

  // Fetch matching courses once the level is known.
  useEffect(() => {
    if (stage !== 'result' || !level) return;
    void getLearningModules({ level })
      .then((result) => setCourses(result?.items ?? []))
      .catch(() => setCourses([]));
  }, [stage, level]);

  if (stage === 'intro') {
    return (
      <div className="vn-place__card">
        <h2 className="vn-place__title">{t('placement.introTitle')}</h2>
        <p className="vn-place__copy">{t('placement.introCopy', { total })}</p>
        <button type="button" className="vn-btn vn-btn--primary" onClick={begin}>
          {t('placement.start')}
        </button>
      </div>
    );
  }

  if (stage === 'quiz' && question) {
    const timePercent = questionTime ? (Math.max(secondsLeft, 0) / questionTime) * 100 : 0;
    return (
      <div className="vn-place__card">
        <div className="vn-place__progress">
          <div className="vn-place__progress-head">
            <span className="vn-place__count">
              {t('placement.position', { current: index + 1, total })}
            </span>
            <span
              className={`vn-place__timer${secondsLeft <= 5 ? ' is-low' : ''}`}
              aria-live="polite"
              aria-label={t('placement.timeLeft', { time: formatClock(secondsLeft) })}
            >
              ⏱ {formatClock(secondsLeft)}
            </span>
          </div>
          <div className="vn-progress">
            <div className="vn-progress__bar" style={{ width: `${(index / total) * 100}%` }} />
          </div>
          <div className="vn-place__timerbar" aria-hidden="true">
            <div
              className={`vn-place__timerbar-fill${secondsLeft <= 5 ? ' is-low' : ''}`}
              style={{ width: `${timePercent}%` }}
            />
          </div>
        </div>
        {question.audio ? (
          <div className="vn-place__listen">
            <span className="vn-place__listen-badge">🎧 {t('placement.listening')}</span>
            <button
              type="button"
              className="vn-btn vn-btn--primary vn-place__listen-play"
              onClick={() => speak(question.audio!)}
            >
              🔊 {t('placement.playAudio')}
            </button>
            {!ttsSupported ? (
              <p className="vn-place__listen-fallback">
                {t('placement.listenFallback')} <em>“{question.audio}”</em>
              </p>
            ) : null}
          </div>
        ) : null}
        <h2 className="vn-place__question">{question.question}</h2>
        <div className="vn-place__options">
          {question.options.map((option) => (
            <button
              key={option}
              type="button"
              className={`vn-place__option${chosen === option ? ' is-chosen' : ''}`}
              aria-pressed={chosen === option}
              onClick={() => pick(option)}
            >
              {option}
            </button>
          ))}
        </div>
        <div className="vn-place__foot vn-place__foot--nav">
          <button type="button" className="vn-btn vn-btn--ghost" disabled={index === 0} onClick={goBack}>
            ‹ {t('common.back')}
          </button>
          <span className="vn-place__autohint">{t('placement.autoAdvance')}</span>
          {/* No skipping: forward is only enabled once the question is answered
              (e.g. after coming back to it). Fresh questions advance by picking. */}
          <button
            type="button"
            className="vn-btn vn-btn--secondary"
            disabled={!chosen}
            onClick={goNext}
          >
            {index + 1 === total ? t('placement.seeResult') : t('placement.next')} ›
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'result' && level) {
    const correct = PLACEMENT_QUESTIONS.reduce((sum, q, i) => sum + (picks[i] === q.answer ? 1 : 0), 0);
    return (
      <div className="vn-place__result">
        <div className="vn-place__card vn-place__card--result">
          <p className="vn-place__eyebrow">{t('placement.recommended')}</p>
          <h2 className="vn-place__level">
            {t(`levels.${level}`)}
            <span className="vn-place__cefr">{t('placement.cefr', { level: LEVEL_CEFR[level] })}</span>
          </h2>
          <p className="vn-place__copy">
            {t('placement.answered', { correct, total })} {t(`placement.advice.${level}`)}
          </p>
          <div className="vn-place__actions">
            <Link href={`/learn?level=${level}`} className="vn-btn vn-btn--primary">
              {t('placement.browseLevel', { level: t(`levels.${level}`) })}
            </Link>
            <button type="button" className="vn-btn vn-btn--ghost" onClick={reset}>
              {t('placement.retake')}
            </button>
          </div>
        </div>

        <section className="vn-place__review">
          <h3>{t('placement.reviewTitle')}</h3>
          <ol className="vn-place__review-list">
            {PLACEMENT_QUESTIONS.map((item, qi) => {
              const pickValue = picks[qi] ?? '';
              const isCorrect = pickValue === item.answer;
              return (
                <li key={qi} className={`vn-place__review-item${isCorrect ? ' is-correct' : ' is-wrong'}`}>
                  <p className="vn-place__review-q">
                    <span className="vn-place__review-mark" aria-hidden="true">
                      {isCorrect ? '✓' : '✕'}
                    </span>
                    {qi + 1}. {item.question}
                  </p>
                  {item.audio ? (
                    <p className="vn-place__review-audio">
                      <button
                        type="button"
                        className="vn-place__review-play"
                        onClick={() => speak(item.audio!)}
                        aria-label={t('placement.playAudio')}
                      >
                        🔊
                      </button>
                      <span>“{item.audio}”</span>
                    </p>
                  ) : null}
                  <p className="vn-place__review-a">
                    {t('placement.yourAnswer')}{' '}
                    <span className={isCorrect ? 'is-correct' : 'is-wrong'}>{pickValue || '—'}</span>
                  </p>
                  {!isCorrect ? (
                    <p className="vn-place__review-a">
                      {t('placement.correctAnswer')} <span className="is-correct">{item.answer}</span>
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </section>

        {courses && courses.length > 0 ? (
          <section className="vn-place__courses">
            <h3>{t('placement.startWith')}</h3>
            <div className="vn-catalog__grid">
              {courses.slice(0, 3).map((module) => (
                <CourseCard key={module.id} module={module} />
              ))}
            </div>
          </section>
        ) : null}
      </div>
    );
  }

  return null;
}
